import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { NERA_AUTH_SERVICE } from 'app/core/tokens';
import { AuthorizationService } from 'app/core/services/authorization.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { ADMIN_TIER_ROLES, hasAnyRole, ROLES } from 'app/shared/constants';

/**
 * Auth guard.
 *
 * Protects authenticated routes by checking:
 * - Whether the user is authenticated (via the auth service's `check()` method)
 * - Whether the user object exists (guards against null user with authenticated=true edge case)
 * - Whether the user has at least one non-anonymous role
 *
 * On failure:
 * - Not authenticated → signs out silently, redirects to /sign-in (which hands off to IAM)
 * - No user object → signs out silently, redirects to /sign-in
 * - `me/permissions` hydration failed (flat session roles only) → retries once, then
 *   /errors/unavailable without signing out
 * - No real role (none/GUEST/ANONYMOUS) → redirects to the in-shell /forbidden
 *
 * @example
 * {
 *   path: 'dashboard',
 *   canActivate: [AuthGuard],
 *   loadComponent: () => import('./dashboard/dashboard.component'),
 * }
 */
export const AuthGuard: CanActivateFn | CanActivateChildFn = (
    _route,
    _state
) => {
    const _authService = inject(NERA_AUTH_SERVICE);
    const _router = inject(Router);
    const _authorization = inject(AuthorizationService);
    const returnUrl = _state.url || '/signed-in-redirect';

    return _authService.check().pipe(
        switchMap((authenticate) => {
            // Not authenticated, or authenticated with a null user (edge case):
            // sign out silently and land on /sign-in (which hands off to IAM).
            if (!authenticate.isAuthenticated || !authenticate.user) {
                if (authenticate.isAuthenticated) {
                    console.warn(
                        '[AuthGuard] User is null but isAuthenticated is true. Signing out...'
                    );
                }
                return _authService.signOut(false, true).pipe(
                    switchMap(() =>
                        of(
                            _router.createUrlTree(['/sign-in'], {
                                queryParams: { returnUrl },
                            })
                        )
                    )
                );
            }

            // Fail closed: flat session roles can carry another org's role; never gate on them.
            const hydrated$ = _authorization.hasLoadedPermissions
                ? of(true)
                : _authorization.hydratePermissions(true).pipe(
                      map(() => _authorization.hasLoadedPermissions),
                      catchError(() => of(false))
                  );
            return hydrated$.pipe(
                switchMap((hydrated) =>
                    !hydrated
                        ? of(
                              _router.createUrlTree(['/errors/unavailable'], {
                                  queryParams: { returnUrl },
                              })
                          )
                        : _authService.check().pipe(
                              map((current) =>
                                  hasRealRole(current.user?.roles)
                                      ? true
                                      : _router.createUrlTree(['/forbidden'])
                              )
                          )
                )
            );
        })
    );

    // No real role (none, GUEST or ANONYMOUS only) is denied (handles comma-separated roles).
    // The "real role" allowlist includes every admin-tier code
    // (ADMIN_TIER_ROLES) plus USER/PHOTOGRAPHER/EDITOR.
    // `/forbidden` lives in this shell: exempt it or the redirect loops.
    // Denial stays inside the app shell — `/errors/forbidden` is the bare,
    // shell-less page (same contract as RouteDataAuthorizationGuard / NERA_API_ERROR_CONFIG).
    function hasRealRole(roles: string | string[] | undefined): boolean {
        return (
            !!_state.url?.startsWith('/forbidden') ||
            hasAnyRole(roles, [
                ...ADMIN_TIER_ROLES,
                ROLES.USER,
                ROLES.PHOTOGRAPHER,
                ROLES.EDITOR,
            ])
        );
    }
};
